import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Shield, LogOut, Users, Calendar, Mail, Phone, MapPin, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    paid: 0
  });
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Session timeout: 15 minutes of inactivity
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTimeout = setInterval(() => {
      const idleTime = Date.now() - lastActivity;
      const fifteenMinutes = 15 * 60 * 1000;
      
      if (idleTime > fifteenMinutes) {
        handleLogout();
        toast.error('Session expired due to inactivity. Please login again.');
      }
    }, 60000); // Check every minute

    // Update last activity on any user interaction
    const updateActivity = () => setLastActivity(Date.now());
    window.addEventListener('mousedown', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('scroll', updateActivity);
    window.addEventListener('touchstart', updateActivity);

    return () => {
      clearInterval(checkTimeout);
      window.removeEventListener('mousedown', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('scroll', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
    };
  }, [isAuthenticated, lastActivity]);

  // Check for existing token on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
      fetchBookings(savedToken);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        throw new Error('Invalid credentials');
      }

      const data = await response.json();
      setToken(data.access_token);
      setIsAuthenticated(true);
      localStorage.setItem('admin_token', data.access_token);
      toast.success('Logged in successfully');
      fetchBookings(data.access_token);
    } catch (error) {
      toast.error('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setToken(null);
    setIsAuthenticated(false);
    setBookings([]);
    localStorage.removeItem('admin_token');
    setUsername('');
    setPassword('');
    toast.success('Logged out successfully');
  };

  const fetchBookings = async (authToken) => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/bookings`, {
        headers: {
          'Authorization': `Bearer ${authToken || token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          throw new Error('Session expired. Please login again.');
        }
        throw new Error('Failed to fetch bookings');
      }

      const data = await response.json();
      setBookings(data);

      // Calculate stats
      const stats = {
        total: data.length,
        pending: data.filter(b => b.status === 'pending').length,
        confirmed: data.filter(b => b.status === 'confirmed').length,
        paid: data.filter(b => b.payment_status === 'paid').length,
      };
      setStats(stats);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAF8F5] via-white to-[#FFF9F0] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-2 border-[#D4AF37]/20">
          <CardHeader className="text-center space-y-4 pb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-[#6B0B0B] to-[#8B1515] rounded-2xl flex items-center justify-center">
              <Shield className="text-white" size={32} />
            </div>
            <div>
              <CardTitle className="text-3xl font-bold text-[#0B090A]">Admin Login</CardTitle>
              <CardDescription className="text-base mt-2">
                Cape Atlantic Tours - Secure Dashboard Access
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username" className="text-gray-700 font-medium">Username</Label>
                <Input
                  id="username"
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="mt-2 border-gray-300 focus:border-[#6B0B0B] rounded-lg"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-gray-700 font-medium">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="mt-2 border-gray-300 focus:border-[#6B0B0B] rounded-lg"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#6B0B0B] to-[#8B1515] hover:from-[#8B1515] hover:to-[#6B0B0B] text-white py-6 text-base font-semibold rounded-xl shadow-lg"
              >
                {loading ? 'Logging in...' : 'Login to Dashboard'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF8F5] via-white to-[#FFF9F0]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#6B0B0B] to-[#8B1515] text-white py-6 shadow-lg">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-white/80 mt-1">Cape Atlantic Tours Management</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20"
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-[#6B0B0B]">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Total Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#6B0B0B]">{stats.total}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-amber-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Pending</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">{stats.pending}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-green-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Confirmed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{stats.confirmed}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Paid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{stats.paid}</div>
            </CardContent>
          </Card>
        </div>

        {/* Action Bar */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Recent Bookings</h2>
          <Button
            onClick={() => fetchBookings()}
            disabled={loading}
            className="bg-[#6B0B0B] hover:bg-[#8B1515]"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        {/* Bookings Table */}
        <div className="space-y-4">
          {loading && bookings.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-gray-500">Loading bookings...</p>
              </CardContent>
            </Card>
          ) : bookings.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-500 text-lg">No bookings found</p>
              </CardContent>
            </Card>
          ) : (
            bookings.map((booking) => (
              <Card key={booking.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Customer Info */}
                    <div>
                      <h3 className="font-semibold text-lg text-[#6B0B0B] mb-3 flex items-center">
                        <Users size={20} className="mr-2" />
                        Customer Details
                      </h3>
                      <div className="space-y-2 text-sm">
                        <p className="flex items-center">
                          <span className="font-medium w-24">First Name:</span>
                          <span className="text-gray-700">{booking.firstName}</span>
                        </p>
                        <p className="flex items-center">
                          <span className="font-medium w-24">Surname:</span>
                          <span className="text-gray-700">{booking.surname}</span>
                        </p>
                        <p className="flex items-center">
                          <span className="font-medium w-24">ID/Passport:</span>
                          <span className="text-gray-700">{booking.idOrPassport}</span>
                        </p>
                        <p className="flex items-center">
                          <Mail size={14} className="mr-2 text-gray-400" />
                          <span className="text-gray-700">{booking.email}</span>
                        </p>
                        <p className="flex items-center">
                          <Phone size={14} className="mr-2 text-gray-400" />
                          <span className="text-gray-700">{booking.phone}</span>
                        </p>
                      </div>
                    </div>

                    {/* Booking Info */}
                    <div>
                      <h3 className="font-semibold text-lg text-[#6B0B0B] mb-3 flex items-center">
                        <Calendar size={20} className="mr-2" />
                        Booking Details
                      </h3>
                      <div className="space-y-2 text-sm">
                        <p>
                          <span className="font-medium">Tour ID:</span>{' '}
                          <span className="text-gray-700">{booking.tourId}</span>
                        </p>
                        <p>
                          <span className="font-medium">Date:</span>{' '}
                          <span className="text-gray-700">{booking.date}</span>
                        </p>
                        <p>
                          <span className="font-medium">Guests:</span>{' '}
                          <span className="text-gray-700">{booking.guests}</span>
                        </p>
                        <p>
                          <span className="font-medium">Created:</span>{' '}
                          <span className="text-gray-700">{formatDate(booking.created_at)}</span>
                        </p>
                      </div>
                    </div>

                    {/* Status */}
                    <div>
                      <h3 className="font-semibold text-lg text-[#6B0B0B] mb-3 flex items-center">
                        <DollarSign size={20} className="mr-2" />
                        Status
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm font-medium">Booking Status:</span>
                          <span
                            className={`ml-2 px-3 py-1 rounded-full text-xs font-semibold ${
                              booking.status === 'confirmed'
                                ? 'bg-green-100 text-green-700'
                                : booking.status === 'pending'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {booking.status?.toUpperCase() || 'PENDING'}
                          </span>
                        </div>
                        <div>
                          <span className="text-sm font-medium">Payment:</span>
                          <span
                            className={`ml-2 px-3 py-1 rounded-full text-xs font-semibold ${
                              booking.payment_status === 'paid'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {booking.payment_status?.toUpperCase() || 'PENDING'}
                          </span>
                        </div>
                        {booking.message && (
                          <div className="mt-3 p-3 bg-gray-50 rounded">
                            <p className="text-xs font-medium text-gray-600 mb-1">Message:</p>
                            <p className="text-sm text-gray-700">{booking.message}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
